import { isRecord } from '@Core/utils/recordGuards';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';

const EVENT_SCHEDULE_KEYS = ['start', 'end', 'all_day', 'recurrence'] as const;

type EventScheduleKey = (typeof EVENT_SCHEDULE_KEYS)[number];

export type RecurrenceValue = {
  rule: string;
  exceptions: string[];
  /** Read only: the date a pending series change takes effect, on a series occurrence. */
  changes_on?: string | null;
};

export type EventScheduleValue = {
  start: string;
  end: string;
  all_day: boolean;
  recurrence: RecurrenceValue | null;
};

export function isRecurrenceValue(value: unknown): value is RecurrenceValue {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.rule === 'string' &&
    value.rule.trim() !== '' &&
    Array.isArray(value.exceptions) &&
    value.exceptions.every((item) => typeof item === 'string')
  );
}

export function isEventScheduleValue(value: unknown): value is EventScheduleValue {
  if (!isRecord(value)) {
    return false;
  }

  const keys = Object.keys(value) as EventScheduleKey[];
  if (keys.length !== EVENT_SCHEDULE_KEYS.length) {
    return false;
  }

  if (!EVENT_SCHEDULE_KEYS.every((key) => keys.includes(key))) {
    return false;
  }

  if (typeof value.start !== 'string' || typeof value.end !== 'string') {
    return false;
  }

  if (typeof value.all_day !== 'boolean') {
    return false;
  }

  if (!isValid(parseISO(value.start)) || !isValid(parseISO(value.end))) {
    return false;
  }

  return value.recurrence === null || isRecurrenceValue(value.recurrence);
}
